import React, { Suspense, Fragment } from 'react';
import { Outlet } from 'react-router-dom';
import Loader from '../loader/Loader.component';
import {
    XMarkIcon,
    IdentificationIcon,
    QrCodeIcon,
    BoltIcon,
    UserIcon,
    ScaleIcon,
    PaperAirplaneIcon,
    PrinterIcon,
    TrashIcon,
    BriefcaseIcon,
    ViewColumnsIcon,
    UserGroupIcon,
    UsersIcon,
    CalendarDaysIcon,
    CheckCircleIcon,
    EnvelopeIcon,
    ArchiveBoxIcon,
    DocumentTextIcon,
    CurrencyDollarIcon,
    MegaphoneIcon,
    ChartBarIcon,
    FunnelIcon,
    AtSymbolIcon,
    NewspaperIcon,
    Cog6ToothIcon,
    ClipboardDocumentCheckIcon,
    LinkIcon,
    PresentationChartLineIcon,
    PresentationChartBarIcon,
    ArrowPathRoundedSquareIcon,
    SignalIcon,
    SwatchIcon,
    PhotoIcon
} from '@heroicons/react/24/outline';
import { NavigationItem } from './Dashboard.types';
import { BrandIcons } from '../UI';
import { Navigator } from './navigation/Navigation.component';
import { AppHeader } from './navigation/AppHeader.component';
import { NotificationBar } from './navigation/NotificationBar.component';
import { WhatsNewModal } from './WhatsNewModal.component';
import { StorefrontDomainModal } from './StorefrontDomainModal.component';
import { TermsOfServiceGate } from './TermsOfServiceGate.component';
import { RouteConstants } from '../../Routes';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { translationsAtom, useCapabilitiesReady, useLogin, useTranslationLanguageKeys, useTranslationLanguages, useUsers, verifyStatusAtom } from '../../services';
import { useAtom } from 'jotai';
import { useGorgiasChat, useSidebarCollapsed, useSnackbar } from '../../hooks';
import { isHighAccess } from '../../utils/permissions';
import { Snackbar, HelpPanel } from '../UI';
import { ReportErrorSlideover } from '../error-report/ReportErrorSlideover.component';
import { Dialog, Transition } from '@headlessui/react';
import { ViewAsBanner } from '../view_as/ViewAsBanner.component';
import { viewAsState } from '../../services/view_as/boot';
import { SendQueueToasts } from '../../pages/sales/shared/send_queue';

/**
 * Sidebar order is a product decision, not an accident — it runs from the thing
 * an artist opens every day (Dashboard, Designs) down to staff-only tooling.
 *
 * Everything a SUPERADMIN used to reach from a loose top-level entry — mockups,
 * brand analytics, translations, checklists, flags, announcements, the whole
 * inventory manager, JDML, the dropper, analytics, cancellations — now lives in
 * /admin-tools. The sidebar is for work; admin-tools is for administration.
 */
const navigationDefault: NavigationItem[] = [
    { name: 'NAVIGATION.DASHBOARD', href: RouteConstants.HOME, icon: BrandIcons.dashboard, current: true, expanded: false, permissionLevel: 'EVERYBODY' },
    // Real brands' growth as case studies, for new artists and stores. Featured: right under Dashboard, with a "New" pill.
    { name: 'NAVIGATION.INSPO', fallbackLabel: 'Inspo', href: RouteConstants.INSPO, icon: SparklesIcon, current: false, expanded: false, permissionLevel: 'USER', featured: true },
    // Always reachable, so a brand that hid the Home pricing card can come back to it.
    { name: 'NAVIGATION.PRICING', fallbackLabel: 'Pricing', href: RouteConstants.PRICING_GUIDE, icon: CurrencyDollarIcon, current: false, expanded: false, permissionLevel: 'EVERYBODY' },
    { name: 'NAVIGATION.DESIGN_CREATION', fallbackLabel: 'Designs', href: RouteConstants.DESIGN_CREATION, icon: BrandIcons.designs, current: false, expanded: false, permissionLevel: 'USER' },
    {
        name: 'NAVIGATION.ORDERS_GROUP', fallbackLabel: 'Orders', href: RouteConstants.ORDERS, icon: BrandIcons.orders, current: false, expanded: false, permissionLevel: 'EVERYBODY', children: [
            // The group header is not shopify-gated, because Place Order lives
            // under it and must reach artists with no storefront.
            //
            // This child is deliberately NOT requiresShopify either, even though
            // /orders is Shopify-gated: it shares the group header's href, so
            // gating only the child produced a group whose header navigated
            // somewhere its own sub-item claimed did not exist. Both now go to
            // /orders, where withShopifyRequired explains how to connect a store.
            { name: 'NAVIGATION.ORDERS', fallbackLabel: 'Orders', href: RouteConstants.ORDERS, icon: BrandIcons.orders, current: false, permissionLevel: 'EVERYBODY' },
            // Deliberately NOT requiresShopify: ordering prints of your own designs
            // is an artist's primary route to physical product.
            { name: 'NAVIGATION.PLACE_ORDER', fallbackLabel: 'Place Order', href: RouteConstants.INTERNAL_ORDER_NEW, icon: PrinterIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.CUSTOMERS', fallbackLabel: 'Customers', href: RouteConstants.CUSTOMERS, icon: UserIcon, current: false, permissionLevel: 'EVERYBODY', requiresShopify: true }
        ]
    },
    {
        name: 'NAVIGATION.LICENSING', fallbackLabel: 'Licensing', href: RouteConstants.PRODUCT_LISTING, icon: BrandIcons.licensing, current: false, expanded: false, permissionLevel: 'USER', children: [
            // Deliberately NOT requiresShopify: licensing is capability-driven and
            // available to brands with no storefront.
            { name: 'NAVIGATION.LICENSING_SETUP', fallbackLabel: 'Licensing Setup', href: RouteConstants.LICENSING_SETUP, icon: BrandIcons['licensed-designs'], current: false, permissionLevel: 'USER' },
            { name: 'NAVIGATION.PRODUCT_LISTING_BROWSE', fallbackLabel: 'Browse Designs', href: RouteConstants.PRODUCT_LISTING, icon: BrandIcons['browse-designs'], current: false, permissionLevel: 'USER' },
            { name: 'NAVIGATION.LICENSED_DESIGNS', fallbackLabel: 'Licensed Designs', href: RouteConstants.LICENSED_DESIGNS, icon: BrandIcons['licensed-designs'], current: false, permissionLevel: 'USER' },
            // Always visible; the page itself explains the empty state.
            { name: 'NAVIGATION.EARNINGS', fallbackLabel: 'Earnings', href: RouteConstants.EARNINGS, icon: BrandIcons.earnings, current: false, permissionLevel: 'USER' }
        ]
    },
    {
        name: 'NAVIGATION.INTERIOR_DESIGN_STUDIO', fallbackLabel: 'Interior Design Studio', href: RouteConstants.DESIGNER_CATALOG, icon: BrandIcons['interior-studio'], current: false, expanded: false, permissionLevel: 'EVERYBODY', featureFlag: 'enable-interior-designer', children: [
            { name: 'NAVIGATION.DESIGNER_CATALOG', fallbackLabel: 'Design Catalog', href: RouteConstants.DESIGNER_CATALOG, icon: BrandIcons['design-catalog'], current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.PROJECTS', fallbackLabel: 'Design Projects', href: RouteConstants.INTERIOR_PROJECTS, icon: BrandIcons['design-projects'], current: false, permissionLevel: 'EVERYBODY' },
            // Sample bundles are intentionally NOT listed here: a bundle only makes
            // sense in the context of a project, so it is reached from the project
            // detail page. The routes still exist for that flow.
            { name: 'NAVIGATION.INTERIOR_BRANDING', fallbackLabel: 'Branding', href: RouteConstants.INTERIOR_BRANDING, icon: BrandIcons['design-catalog'], current: false, permissionLevel: 'EVERYBODY' }
        ]
    },
    { name: 'NAVIGATION.INVOICES', fallbackLabel: 'Invoices', href: RouteConstants.INVOICES, icon: BrandIcons.invoices, current: false, expanded: false, permissionLevel: 'ADMIN' },
    { name: 'NAVIGATION.USERS', fallbackLabel: 'Users', href: RouteConstants.USERS, icon: BrandIcons.users, current: false, children: [], expanded: false, permissionLevel: 'ADMIN' },
    // permissionLevel is EVERYBODY on purpose: the real gate is requiresSalesAccess,
    // a grant in order.sales_reps. Leaving the tier permissive avoids touching the
    // non-hierarchical hasPermission switch — see Navigation.component.tsx.
    {
        name: 'NAVIGATION.SALES', fallbackLabel: 'Sales', href: RouteConstants.SALES_INBOX, icon: BriefcaseIcon,
        current: false, expanded: false, permissionLevel: 'EVERYBODY', requiresSalesAccess: true,
        children: [
            { name: 'NAVIGATION.SALES_PEOPLE', fallbackLabel: 'People', href: RouteConstants.SALES_INBOX, icon: UserGroupIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_PIPELINE', fallbackLabel: 'Pipeline', href: RouteConstants.SALES_PIPELINE, icon: ViewColumnsIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_EMAIL', fallbackLabel: 'Email', href: RouteConstants.SALES_MESSAGES, icon: EnvelopeIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_CALENDAR', fallbackLabel: 'Calendar', href: RouteConstants.SALES_CALENDAR, icon: CalendarDaysIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_TASKS', fallbackLabel: 'Tasks', href: RouteConstants.SALES_TASKS, icon: CheckCircleIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_TEAM', fallbackLabel: 'Sales team', href: RouteConstants.SALES_TEAM, icon: UsersIcon, current: false, permissionLevel: 'EVERYBODY', requiresSalesManager: true },
            { name: 'NAVIGATION.SALES_DECKS', fallbackLabel: 'Decks', href: RouteConstants.SALES_DECKS, icon: PresentationChartBarIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_TEMPLATES', fallbackLabel: 'Email templates', href: RouteConstants.SALES_TEMPLATES, icon: DocumentTextIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.SALES_SIGNATURE', fallbackLabel: 'Email signature', href: RouteConstants.SALES_SIGNATURE, icon: IdentificationIcon, current: false, permissionLevel: 'EVERYBODY' }
        ]
    },
    // Email marketing rides on the same sales grant; settings are for sales managers.
    {
        name: 'NAVIGATION.MARKETING', fallbackLabel: 'Marketing', href: RouteConstants.MARKETING, icon: MegaphoneIcon,
        current: false, expanded: false, permissionLevel: 'EVERYBODY', requiresSalesAccess: true,
        children: [
            { name: 'NAVIGATION.MARKETING_OVERVIEW', fallbackLabel: 'Overview', href: RouteConstants.MARKETING, icon: ChartBarIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_CAMPAIGNS', fallbackLabel: 'Campaigns', href: RouteConstants.MARKETING_CAMPAIGNS, icon: PaperAirplaneIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_FLOWS', fallbackLabel: 'Flows', href: RouteConstants.MARKETING_FLOWS, icon: ArrowPathRoundedSquareIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_REVIEW', fallbackLabel: 'Review', href: RouteConstants.MARKETING_REVIEW, icon: ClipboardDocumentCheckIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_SEGMENTS', fallbackLabel: 'Segments', href: RouteConstants.MARKETING_SEGMENTS, icon: FunnelIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_SIGNALS', fallbackLabel: 'Signals', href: RouteConstants.MARKETING_SIGNALS, icon: SignalIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_CONTACTS', fallbackLabel: 'Contacts', href: RouteConstants.MARKETING_CONTACTS, icon: AtSymbolIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_TEMPLATES', fallbackLabel: 'Templates', href: RouteConstants.MARKETING_TEMPLATES, icon: NewspaperIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_BRAND', fallbackLabel: 'Brand', href: RouteConstants.MARKETING_BRAND, icon: SwatchIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_ASSETS', fallbackLabel: 'Assets', href: RouteConstants.MARKETING_ASSETS, icon: PhotoIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_LINKS', fallbackLabel: 'Links', href: RouteConstants.MARKETING_LINKS, icon: LinkIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_ATTRIBUTION', fallbackLabel: 'Attribution', href: RouteConstants.MARKETING_ATTRIBUTION, icon: PresentationChartLineIcon, current: false, permissionLevel: 'EVERYBODY' },
            { name: 'NAVIGATION.MARKETING_SETTINGS', fallbackLabel: 'Settings', href: RouteConstants.MARKETING_SETTINGS, icon: Cog6ToothIcon, current: false, permissionLevel: 'EVERYBODY', requiresSalesManager: true }
        ]
    },
    {
        name: 'NAVIGATION.PRODUCTION', fallbackLabel: 'Production', href: RouteConstants.SCANNER, icon: BoltIcon, current: false, expanded: false, permissionLevel: 'TECHNICIAN', children: [
            { name: 'NAVIGATION.SCANNER', fallbackLabel: 'Scanner', href: RouteConstants.SCANNER, icon: QrCodeIcon, current: false, permissionLevel: 'TECHNICIAN' },
            { name: 'NAVIGATION.WEIGH_STATION', fallbackLabel: 'Weigh Station', href: RouteConstants.WEIGH_STATION, icon: ScaleIcon, current: false, permissionLevel: 'TECHNICIAN' },
            { name: 'NAVIGATION.SHIP_STATION', fallbackLabel: 'Ship Station', href: RouteConstants.SHIP_STATION, icon: PaperAirplaneIcon, current: false, permissionLevel: 'TECHNICIAN' },
            { name: 'NAVIGATION.WASTE_TRACKING', fallbackLabel: 'Waste Tracking', href: RouteConstants.WASTE_TRACKING, icon: TrashIcon, current: false, permissionLevel: 'TECHNICIAN' },
            // Moved out of Admin Tools. One entry for the whole manager; its own
            // tab bar (InventoryLayout) handles the sub-screens, and the sidebar
            // stays highlighted across them via prefix matching.
            { name: 'NAVIGATION.INVENTORY', fallbackLabel: 'Inventory', href: RouteConstants.PRODUCTION_INVENTORY, icon: ArchiveBoxIcon, current: false, permissionLevel: 'TECHNICIAN' },
            // Brands stays SUPERADMIN-only inside an otherwise TECHNICIAN group;
            // child role filtering hides it from everyone below that.
            { name: 'NAVIGATION.BRANDS', fallbackLabel: 'Brands', href: RouteConstants.BRANDS, icon: IdentificationIcon, current: false, permissionLevel: 'SUPERADMIN' }
        ]
    }
]

const Dashboard: React.FunctionComponent<{}> = (): React.ReactElement => {

    const [, , TranslationKeyFunctions] = useTranslationLanguageKeys();
    const [languages, , LanguageKeyFunctions] = useTranslationLanguages();

    const [sidebarOpen, setSidebarOpen] = React.useState(false);
    const [navigation, setNavigation] = React.useState(navigationDefault);
    const [, setTranslations] = useAtom(translationsAtom);
    const { collapsed: sidebarCollapsed } = useSidebarCollapsed();

    const [snackbar, setSnackbar] = useSnackbar();
    const [user, authToken] = useLogin();
    const [, , AuthFunctions] = useUsers();
    const [verifyStatus, setVerifyStatus] = useAtom(verifyStatusAtom);

    // Populates the shared capability atom once for the whole shell and keeps it
    // fresh on window focus, so a Shopify connect promotes the layout without a
    // re-login. Consumers read the atom rather than taking props.
    useCapabilitiesReady();

    // The support widget is for customers only. Staff (technicians and both
    // super tiers) run workstation screens where a floating bubble covers the
    // controls, so it is suppressed for them outright — not merely hidden on
    // the inventory routes, which is what the old path check did.
    useGorgiasChat(isHighAccess(user));

    const imagesBucket = process.env.REACT_APP_AWS_BUCKET_IMAGES || '';

    // These hooks rebuild their function objects every render, and
    // `checkVerifyStatus` closes over `user`/`verifyStatus` so it changes
    // identity too. Depending on any of them directly would re-run the mount
    // effect (re-injecting the Gorgias script) on every render. Refs give the
    // effects stable deps while still calling through to the latest closure.
    const languageFnsRef = React.useRef(LanguageKeyFunctions);
    languageFnsRef.current = LanguageKeyFunctions;
    const translationFnsRef = React.useRef(TranslationKeyFunctions);
    translationFnsRef.current = TranslationKeyFunctions;
    const checkVerifyStatusRef = React.useRef<() => Promise<void>>();

    // Read once on mount: this effect intentionally captures the initial
    // language list rather than re-running when it changes.
    const languagesLoadedOnMount = React.useRef(languages.length > 0);

    React.useEffect(() => {
        if (!languagesLoadedOnMount.current) {
            languageFnsRef.current.get();
        }

        const language = parseInt(localStorage.getItem('language') || '1');
        translationFnsRef.current.getTranslationsByLanguageId(language).then((translations: { [key: string]: string } | null) => {
            if (translations) {
                setTranslations(translations);
            }
        });

        checkVerifyStatusRef.current?.();
    }, [setTranslations]);

    React.useEffect(() => {
        if (user && !user.email_verified) {
            checkVerifyStatusRef.current?.();
        } else if (user && user.email_verified) {
            setVerifyStatus(true);
        }
    }, [user, setVerifyStatus]);

    const checkVerifyStatus = async () => {
        if (!user) return;

        if (authToken && !verifyStatus) {
            const result = await AuthFunctions.getVerifyStatus(user.id as number);
            const localUser = localStorage.getItem('user');
            if (result !== 100 && localUser && !JSON.parse(localUser).email_verified) {
                localStorage.setItem('user', JSON.stringify({ ...JSON.parse(localUser), email_verified: result }));
                checkVerifyStatus();
            }
        }
    }

    // Assigned during render so both effects above always invoke the latest
    // closure without taking a changing identity as a dependency.
    checkVerifyStatusRef.current = checkVerifyStatus;

    return (
        <div className="w-screen h-screen overflow-x-hidden relative print:h-auto print:overflow-visible">
            {viewAsState() ? <ViewAsBanner /> : null}
            <SendQueueToasts />
            <Snackbar
                colorType={snackbar.snackbarLevel}
                show={snackbar.show}
                showUpdate={(value: boolean) => {
                    setSnackbar({ text: snackbar.text, show: value, snackbarLevel: snackbar.snackbarLevel, duration: 5 });
                }}
                text={snackbar.text}
                dissapearIn={snackbar.duration ? snackbar.duration : 5}
            />

            {/* Mobile navigation slide Animation */}
            <Transition.Root show={sidebarOpen} as={Fragment}>
                <Dialog as="div" className="relative lg:hidden" onClose={setSidebarOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="transition-opacity ease-linear duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity ease-linear duration-300"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 dark:bg-stone-900/65 bg-stone-300/65" />
                    </Transition.Child>

                    <div className="fixed inset-0 flex z-50">
                        <Transition.Child
                            as={Fragment}
                            enter="transition ease-in-out duration-300 transform"
                            enterFrom="-translate-x-full"
                            enterTo="translate-x-0"
                            leave="transition ease-in-out duration-300 transform"
                            leaveFrom="translate-x-0"
                            leaveTo="-translate-x-full"
                        >
                            <Dialog.Panel className="relative mr-16 flex w-full max-w-xs flex-1">
                                <Transition.Child
                                    as={Fragment}
                                    enter="ease-in-out duration-300"
                                    enterFrom="opacity-0"
                                    enterTo="opacity-100"
                                    leave="ease-in-out duration-300"
                                    leaveFrom="opacity-100"
                                    leaveTo="opacity-0"
                                >
                                    <div className="absolute left-full top-0 flex w-16 justify-center pt-5">
                                        <button type="button" className="-m-2.5 p-2.5" onClick={() => setSidebarOpen(false)}>
                                            <span className="sr-only">Close sidebar</span>
                                            <XMarkIcon className="h-6 w-6 dark:text-white text-black" aria-hidden="true" />
                                        </button>
                                    </div>
                                </Transition.Child>

                                <Navigator
                                    navigation={navigation}
                                    setNavigation={(results: NavigationItem[]) => {
                                        setNavigation(results);
                                    }}
                                    closeSidebar={setSidebarOpen}
                                />

                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* desktop static navigation */}
            <div className="hidden lg:block">
                <Navigator
                    navigation={navigation}
                    setNavigation={(results: NavigationItem[]) => {
                        setNavigation(results);
                    }} closeSidebar={setSidebarOpen}
                />
            </div>

            {/* Main Content Container */}
            <div className="h-screen w-full overflow-hidden flex print:h-auto print:overflow-visible print:block">
                {/* Padding must track the sidebar rail width in Navigation.component.tsx. */}
                {/*
                  * Flex column, NOT `min-h-full` children.
                  *
                  * AppHeader is `sticky`, so it occupies height in this scroll
                  * container. A `min-h-full` sibling below it therefore measured
                  * 100% of the viewport *in addition to* the header and the
                  * notification bar, which overflowed by exactly the header
                  * height and put a scrollbar on every page in the app even when
                  * the content was half a screen tall. The old `pb-20`/`pb-28`
                  * pair was compensating for that, adding ~12rem of dead space
                  * below the footer logo.
                  *
                  * `flex-1` on the content wrapper achieves the original intent
                  * — footer logo pinned to the bottom on short pages, pushed down
                  * on long ones — without ever exceeding the container.
                  */}
                <main className={`h-full flex-1 overflow-y-auto content-scroll flex flex-col transition-[padding] duration-200 ease-out ${sidebarCollapsed ? 'lg:pl-[4.5rem]' : 'lg:pl-72'}`}>
                    <AppHeader setSidebarOpen={setSidebarOpen} />
                    <NotificationBar />
                    <div className="flex-1 px-2 sm:px-2 lg:px-8 flex flex-col gap-y-8 justify-between">
                        <div className="w-[98%] md:w-[96%] lg:w-full lg:p-6">
                            <Suspense fallback={
                                <div className="flex flex-row justify-center">
                                    <Loader />
                                </div>
                            }>
                                <Outlet />
                            </Suspense>
                        </div>
                        <div className="flex flex-row justify-center pb-6 shrink-0">
                            <img
                                className="w-[258px] h-[58px] block dark:hidden"
                                src={`https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Logo-light-mode.png`}
                            />
                            <img
                                className="w-[258px] h-[58px] hidden dark:block"
                                src={`https://${imagesBucket}.s3.us-west-1.amazonaws.com/dashboard-images/Wallmates-Logo-dark-mode.png`}
                            />
                        </div>
                    </div>
                </main>
                <HelpPanel />
                {/* Report an Error form. Triggered from the header account menu. */}
                <ReportErrorSlideover />
            </div>
            <WhatsNewModal />
            <StorefrontDomainModal />
            {/* Last so it stacks above the other self-gating modals. */}
            <TermsOfServiceGate />
        </div>
    )
}

export default Dashboard;
